import SentEmailPopup from '../../components/Popup/SentEmailPopup';
import axiosClient from '../../api/axiosClient';
import { runTokenLogin, markTokenLoginExhausted } from './tokenLoginAttempts';
import { cookieDecryption, cookieEncryption, onBoardingHandler, handleLogOut } from '../../utils';
import { buildUserDetailsCookiePayload } from '../../utils/authCookiePayload';
import { PROFILE_QUESTIONS_SAVE_ERROR } from '../../utils/profileQuestions';
import {
  REGISTRATION_LINK_ERROR,
  loginPathForRegistrationError,
  registrationLinkErrorFromAxiosError,
  registrationLinkErrorFromBody,
  registrationSubmitRateLimitedMessage,
  retryAfterSeconds,
} from '../../utils/registrationLinkError';
import { AUTH_REQUEST_TIMEOUT_MS, authRequestErrorMessage } from '../../utils/authRequestError';
import PasswordResetSuccess from '../../components/Popup/PasswordResetSuccess';

/**
 * Base used only to resolve a candidate redirect. Never navigated to.
 *
 * '.invalid' is reserved by RFC 2606, so it can never be a real host.
 */
const REDIRECT_PARSE_BASE = 'https://redirect.invalid';

/**
 * True when `value` still points at our own origin once a browser parses it.
 *
 * Prefix checks alone are not enough, because the browser normalises the string
 * before it resolves it:
 *  - TAB, LF and CR are stripped, so '/\t//evil.example' becomes
 *    '///evil.example' and leaves the origin.
 *  - Dot segments collapse, so '/.//evil.example' becomes the
 *    protocol-relative '//evil.example'.
 * So resolve the value with the same parser the browser uses and check the
 * result, instead of guessing at the rules.
 *
 * @param {string} value
 * @returns {boolean}
 */
const isRelativePath = value => {
  // Control characters have no place in a path, and the parser drops some of
  // them rather than rejecting the value.
  if (/[\u0000-\u001f\u007f]/.test(value)) return false;
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  if (value.includes('://') || value.includes('\\')) return false;
  let resolved;
  try {
    resolved = new URL(value, REDIRECT_PARSE_BASE);
  } catch {
    return false;
  }
  // A resolved path of '//host' is protocol-relative once it is used as a URL.
  return resolved.origin === REDIRECT_PARSE_BASE && !resolved.pathname.startsWith('//');
};

/**
 * True when a redirect value names a real destination we can navigate to.
 *
 * SECURITY: this must reject anything that could leave the origin. The backend
 * sanitizes the value it echoes, but the MFA hop carries a redirect straight
 * from the login page's query string, so it never passes through that check —
 * '//evil.example' starts with a slash and would otherwise be accepted.
 * Checks both the raw and the decoded form, so '/%2f%2fevil.example' and
 * '/%09//evil.example' are rejected too.
 *
 * The '/' sentinel is a real destination but not a useful one: sanitizeRedirect
 * returns it whenever the request carried no usable redirect, so honouring it
 * would bounce every user through the login route.
 *
 * @param {unknown} redirect
 * @returns {boolean}
 */
const isUsableRedirect = redirect => {
  if (typeof redirect !== 'string' || redirect === '/' || redirect.length > 500) return false;
  if (!isRelativePath(redirect)) return false;
  try {
    return isRelativePath(decodeURIComponent(redirect));
  } catch {
    // Invalid percent-encoding is suspicious — reject.
    return false;
  }
};

/**
 * Picks where to send a user after a successful login.
 *
 * Precedence: the redirect the backend echoed, then one carried across the MFA
 * hop, then the landing page for the user's onboarding step.
 *
 * @param {object} data login response payload
 * @param {string} [pendingRedirect] destination carried through /mfa
 * @returns {string} path to navigate to
 */
export const resolvePostLoginPath = (data, pendingRedirect) => {
  if (isUsableRedirect(data?.redirect)) return data.redirect;
  if (isUsableRedirect(pendingRedirect)) return pendingRedirect;
  return onBoardingHandler(data?.onboardingStep ?? data?.userObj?.onboardingStep);
};

export const createInitialAuthState = () => {
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);

  return {
    logInUser: {},
    isCookiePopup: true,
    notificationSessionPending: Boolean(
      params.get('token') || (params.get('shortLivedToken') && !cookieDecryption('userDetails'))
    ),
  };
};

const authModel = {
  state: createInitialAuthState(),
  reducers: {
    setUserDetails(state, payload) {
      return {
        ...state,
        [payload.name]: payload.data,
      };
    },
  },
  effects: dispatch => ({
    async userLogin({ payload, handleNavigate, handleMfaNavigate }) {
      this.setUserDetails({ name: 'notificationSessionPending', data: true });
      const trust = (() => {
        try {
          return localStorage.getItem('mfa_trust') || '';
        } catch {
          return '';
        }
      })();

      const body = trust ? { ...payload, trustToken: trust } : payload;

      return await axiosClient
        .post('/auth/login', body, { timeout: AUTH_REQUEST_TIMEOUT_MS })
        .then(async res => {
          if (res.data.code === 200) {
            if (res?.data?.data?.mfaRequired) {
              handleMfaNavigate && handleMfaNavigate({ mfaToken: res?.data?.data?.mfaToken, email: payload?.email });
              return { data: res.data.data };
            }

            try {
              if (res?.data?.data?.trustToken) localStorage.setItem('mfa_trust', res.data.data.trustToken);
              if (res?.data?.data?.samlAutoCreated === true && typeof res?.data?.data?.email !== 'string') {
                localStorage.setItem('allowEmailEdit', 'true');
              } else {
                localStorage.removeItem('allowEmailEdit');
              }
            } catch {}

            localStorage.setItem('userStatus', 'logged In');

            // Capture full login data before cookie-slim deletions below so that
            // the SAML auto-created second dispatch can still spread partnerAdmin/region.
            const logInUserData = {
              ...res.data.data,
              _id: res.data.data._id ?? res.data.data.userId,
              userId: res.data.data._id ?? res.data.data.userId,
              allowEmailEdit: !!(
                res?.data?.data?.samlAutoCreated === true && typeof res?.data?.data?.email !== 'string'
              ),
            };

            this.setUserDetails({
              name: 'logInUser',
              data: logInUserData,
            });

            // Don't store partnerAdmin and region in cookie
            delete res.data.data.partnerAdmin;
            delete res.data.data.region;
            cookieEncryption('userDetails', res.data.data);
            this.setUserDetails({ name: 'notificationSessionPending', data: false });

            // If SSO user was auto-created without email, preserve flag in state for UI to allow email input
            if (res?.data?.data?.samlAutoCreated === true) {
              this.setUserDetails({ name: 'logInUser', data: { ...logInUserData, samlAutoCreated: true } });
            }
            handleNavigate(resolvePostLoginPath(res.data.data));
          } else {
            const mappedMsg =
              res.data.msg === 'No account found, please contact your admin.'
                ? 'Your partner has been archived and is no longer active. Please contact your system administrator for access.'
                : res.data.msg;

            if (res.data.code !== 404 && res.data.code !== 400) {
              dispatch.toastModel.showToast({
                value: 'Danger',
                message: mappedMsg,
              });
            }
            return { error: true, state: res.data.code, message: mappedMsg };
          }
        })
        .catch(err => {
          const respMsg = err?.response?.data?.msg;
          const mappedErr =
            respMsg === 'No account found, please contact your admin.'
              ? 'Your partner has been archived and is no longer active. Please contact your system administrator for access.'
              : respMsg;
          // IMP-2558: the page shows requestErr inline, so a 429 or a dropped request is not silent.
          const requestErr = authRequestErrorMessage(err);
          return {
            error: true,
            state: 403,
            message: requestErr ?? mappedErr,
            requestError: Boolean(requestErr),
          };
        });
    },

    async userGhostLogin({ payload, handleNavigate }) {
      this.setUserDetails({ name: 'notificationSessionPending', data: true });
      // Bounded + single-flight per exact token (2026-08-24 incident: a failing
      // loginViaToken call retried forever because the token stayed in the URL
      // and every remount re-dispatched this effect). See tokenLoginAttempts.js.
      const attemptKey = `ghost:${payload?.token ?? ''}`;
      const attempt = await runTokenLogin(attemptKey, () =>
        axiosClient.post(`/auth/loginViaToken?token=${payload?.token}`)
      );

      if (attempt.status === 'blocked') {
        return;
      }
      if (attempt.status === 'failed') {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: "We couldn't sign you in with this link. Please try again, or log in with your email and password.",
        });
        handleNavigate('/');
        return;
      }

      const res = attempt.res;
      try {
        if (res.data.code === 200) {
          if (res.data?.data?.pairImported) {
            this.setUserDetails({ name: 'notificationSessionPending', data: false });
            handleNavigate(`/register/?id=${res.data?.data?.userId}`);

            dispatch.toastModel.showToast({
              value: 'Danger',
              message: res.data.msg,
            });
            dispatch.authModel.getUserDetails({});
          } else {
            localStorage.setItem('userStatus', 'login via token');

            this.setUserDetails({
              name: 'logInUser',
              data: {
                ...res.data.data,
                _id: res.data.data._id ?? res.data.data.userId,
                userId: res.data.data._id ?? res.data.data.userId,
              },
            });

            // Don't store partnerAdmin and region in cookie
            delete res.data.data.partnerAdmin;
            delete res.data.data.region;
            cookieEncryption('userDetails', res.data.data);
            this.setUserDetails({ name: 'notificationSessionPending', data: false });

            handleNavigate(onBoardingHandler(res.data.data.onboardingStep));
          }
        } else {
          // The token itself is bad (e.g. code 404 in a 200 response) — never
          // re-attempt it, and navigate away from the token-bearing URL so a
          // remount can't re-dispatch into the API again.
          markTokenLoginExhausted(attemptKey);
          dispatch.toastModel.showToast({
            value: 'Danger',
            message: res.data.msg,
          });
          handleNavigate('/');
        }
      } catch (err) {
        // Malformed/unexpected response body. Previously swallowed silently by
        // an empty .catch; treat as terminal so nothing can loop on it.
        markTokenLoginExhausted(attemptKey);
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: "We couldn't sign you in with this link. Please try again, or log in with your email and password.",
        });
        handleNavigate('/');
      }
    },

    async userGhostLoginShort({ payload, handleNavigate, handleMfaNavigate }) {
      this.setUserDetails({ name: 'notificationSessionPending', data: true });
      // Same bounded/single-flight guard as userGhostLogin — the SAML
      // short-lived-token exchange has the identical remount-refire shape.
      const attemptKey = `short:${payload?.token ?? ''}`;
      const attempt = await runTokenLogin(attemptKey, () =>
        axiosClient.post(
          `/auth/loginViaShortLivedToken?token=${payload?.token}`,
          (() => {
            try {
              const t = localStorage.getItem('mfa_trust');
              return t ? { trustToken: t } : {};
            } catch {
              return {};
            }
          })()
        )
      );

      if (attempt.status === 'blocked') {
        return;
      }
      if (attempt.status === 'failed') {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: "We couldn't sign you in with this link. Please try again, or log in with your email and password.",
        });
        handleNavigate('/');
        return;
      }

      const res = attempt.res;
      try {
        if (res.data.code === 200) {
          // MFA gating for SAML short-lived token exchange
          if (res?.data?.data?.mfaRequired) {
            handleMfaNavigate &&
              handleMfaNavigate({ mfaToken: res?.data?.data?.mfaToken, email: res?.data?.data?.email });
            return;
          }
          if (res.data?.data?.pairImported) {
            this.setUserDetails({ name: 'notificationSessionPending', data: false });
            handleNavigate(`/register/?id=${res.data?.data?.userId}`);

            dispatch.toastModel.showToast({
              value: 'Danger',
              message: res.data.msg,
            });
            dispatch.authModel.getUserDetails({});
          } else {
            cookieEncryption('userDetails', res.data.data);
            try {
              if (res?.data?.data?.trustToken) localStorage.setItem('mfa_trust', res.data.data.trustToken);
              // If user lacks a valid email, allow editing during onboarding
              if (typeof res?.data?.data?.email !== 'string' || !res?.data?.data?.email) {
                localStorage.setItem('allowEmailEdit', 'true');
              } else {
                localStorage.removeItem('allowEmailEdit');
              }
            } catch {}
            localStorage.setItem('userStatus', 'login via token');
            this.setUserDetails({
              name: 'logInUser',
              data: {
                ...res.data.data,
                _id: res.data.data._id ?? res.data.data.userId,
                userId: res.data.data._id ?? res.data.data.userId,
                allowEmailEdit: !!(typeof res?.data?.data?.email !== 'string' || !res?.data?.data?.email),
              },
            });
            this.setUserDetails({ name: 'notificationSessionPending', data: false });
            handleNavigate(onBoardingHandler(res.data.data.onboardingStep));
          }
        } else {
          // Bad short-lived token — terminal, same reasoning as userGhostLogin.
          markTokenLoginExhausted(attemptKey);
          dispatch.toastModel.showToast({
            value: 'Danger',
            message: res.data.msg,
          });
          handleNavigate('/');
        }
      } catch (err) {
        markTokenLoginExhausted(attemptKey);
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: "We couldn't sign you in with this link. Please try again, or log in with your email and password.",
        });
        handleNavigate('/');
      }
    },

    async getEmail({ payload, setValue, setRole, handleNavigate }) {
      return await axiosClient
        .post('/auth/getUserEmail', payload)
        .then(res => {
          if (res.data.code === 200) {
            const { email, role, ssoRequired } = res.data.data || {};
            if (ssoRequired === true) {
              const msg = 'Please click the Sign in with SSO button and login to complete your registration.';
              try {
                window.location.href = `/?samlError=${encodeURIComponent(msg)}`;
              } catch {
                handleNavigate(`/?samlError=${encodeURIComponent(msg)}`);
              }
              return;
            }
            setValue('email', email);
            setRole(role);
          } else {
            handleNavigate(loginPathForRegistrationError(registrationLinkErrorFromBody(res.data)));
          }
        })
        .catch(err => {
          handleNavigate(
            loginPathForRegistrationError(registrationLinkErrorFromAxiosError(err), retryAfterSeconds(err))
          );
        });
    },

    async userRegistration({ payload, handleNavigate, reset }) {
      return await axiosClient
        .post('/auth/register', payload)
        .then(res => {
          if (res.data.code === 200) {
            reset();
            if (res.data?.data?.userObj?.pairImported) {
              handleNavigate('/application-complete');
              cookieEncryption('userDetails', {
                ...res.data.data.userObj,
                token: res?.data?.data?.token,
              });
              localStorage.setItem('userStatus', 'logged In');
              this.setUserDetails({
                name: 'logInUser',
                data: {
                  ...res.data.data,
                  _id: res.data.data.userObj._id ?? res.data.data.userObj._id,
                  userId: res.data.data.userObj._id ?? res.data.data.userObj._id,
                  preferredFname: res.data.data.userObj.preferredFname ?? res.data.data.userObj.preferredFname,
                  preferredLname: res.data.data.userObj.preferredLname ?? res.data.data.userObj.preferredLname,
                  lname: res.data.data.userObj.lname,
                  role: res.data.data.userObj.role ?? res.data.data.userObj.role,
                  profilePic: res.data.data.userObj.profilePic,
                },
              });
              dispatch.toastModel.showToast({
                value: 'Success',
                message: res.data.msg,
              });
            } else {
              let auth2 = {
                ...res.data.data.userObj,
                token: res?.data?.data?.token,
              };
              const requiredAuth = {
                email: res.data.data.userObj.email,
                fname: res.data.data.userObj.fname,
                lname: res.data.data.userObj.lname,
                preferredFname: res.data.data.userObj.preferredFname,
                preferredLname: res.data.data.userObj.preferredLname,
                onboardingStep: res.data.data.userObj.onboardingStep,
                partnerAdmin: res.data.data.userObj.partnerAdmin,
                primaryPhoneNo: res.data.data.userObj.primaryPhoneNo,
                profilePic: res.data.data.userObj.profilePic,
                profilePicKey: res.data.data.userObj.profilePicKey,
                role: res.data.data.userObj.role,
                timezone: res.data.data.userObj.timezone,
                token: res.data.data.token,
                userId: res.data.data.userObj.userId,
              };
              cookieEncryption('userDetails', requiredAuth);
              localStorage.setItem('userStatus', 'logged In');
              this.setUserDetails({
                name: 'logInUser',
                data: {
                  ...res.data.data,
                  _id: res.data.data.userObj?._id ?? res.data.data.userObj._id,
                  userId: res.data.data.userObj._id ?? res.data.data.userObj._id,
                },
              });
              handleNavigate(onBoardingHandler(res.data.data.userObj.onboardingStep));
            }
          } else {
            dispatch.toastModel.showToast({
              value: 'Danger',
              message: res.data.msg,
            });
            handleNavigate('/');
          }
        })
        .catch(err => {
          const code = registrationLinkErrorFromAxiosError(err);
          if (code === REGISTRATION_LINK_ERROR.RATE_LIMITED) {
            // IMP-2466: stay on the form. Leaving it throws away the invitation link, and the
            // student's details are still valid — they only need to submit again later.
            dispatch.toastModel.showToast({
              value: 'Danger',
              message: registrationSubmitRateLimitedMessage(retryAfterSeconds(err)),
            });
            return;
          }
          handleNavigate('/');
        });
    },

    async requestResetPassword({ payload, reset }) {
      return await axiosClient
        .post('/auth/requestResetPassword', payload, { timeout: AUTH_REQUEST_TIMEOUT_MS })
        .then(res => {
          if (res.data.code === 200) {
            reset();
            dispatch.dialogModel.showDialog({
              component: () => <SentEmailPopup />,
              className: 'forgot_popup_height',
            });
          } else {
            if (res.data.code !== 404 && res.data.code !== 400) {
              dispatch.toastModel.showToast({
                value: 'Danger',
                message: res.data.msg,
              });
            }
            return { error: true, state: res.data.code };
          }
        })
        .catch(err => {
          // IMP-2558: this effect had no catch, so a 429 or a dropped request left the page silent.
          // The page shows `message` inline.
          return {
            error: true,
            state: err?.response?.status ?? 0,
            message: authRequestErrorMessage(err) ?? 'Something went wrong. Please try again.',
          };
        });
    },

    async changePassword({ payload, onContinue }) {
      return await axiosClient
        .post('/auth/forgotPassword', payload, { timeout: AUTH_REQUEST_TIMEOUT_MS })
        .then(res => {
          if (res.data.code === 200) {
            dispatch.dialogModel.showDialog({
              component: () => <PasswordResetSuccess onContinue={onContinue} />,
              className: 'password_popup_height',
            });
          } else {
            dispatch.toastModel.showToast({
              value: 'Danger',
              message: res.data.msg,
            });
          }
        })
        .catch(err => {
          // IMP-2558: shares the password-reset rate-limit bucket with requestResetPassword.
          dispatch.toastModel.showToast({
            value: 'Danger',
            message: authRequestErrorMessage(err) ?? 'Something went wrong. Please try again.',
          });
        });
    },

    // Resolves to { ok, message }. The modal renders the message itself: a toast sits outside the
    // dialog, where screen readers cannot reach it while the dialog is open.
    async submitProfileQuestions({ payload }, rootState) {
      const failed = message => ({ ok: false, message: message || PROFILE_QUESTIONS_SAVE_ERROR });
      let res;
      try {
        res = await axiosClient.post('mentee/profileQuestions', payload);
      } catch (error) {
        return failed(error?.response?.data?.msg);
      }
      if (res.data?.code !== 200) {
        return failed(res.data?.msg);
      }
      const pending = Array.isArray(res.data?.data?.pendingProfileQuestions)
        ? res.data.data.pendingProfileQuestions
        : [];
      this.setUserDetails({
        name: 'logInUser',
        data: { ...rootState.authModel.logInUser, pendingProfileQuestions: pending },
      });
      const auth = cookieDecryption('userDetails');
      if (auth) {
        cookieEncryption('userDetails', { ...auth, pendingProfileQuestions: pending });
      }
      // A success that still lists a question just answered would otherwise look like nothing happened.
      if (Object.keys(payload || {}).some(question => pending.includes(question))) {
        return failed();
      }
      return { ok: true };
    },

    async getUserDetails({}) {
      return await axiosClient.get('auth/getUserDetail').then(response => {
        if (response.data.code === 200) {
          const data = response.data?.data;
          const userObj = data?.userObj;
          if (!userObj) {
            return;
          }
          const auth = cookieDecryption('userDetails');

          const cookiePayload = buildUserDetailsCookiePayload(auth, userObj);
          cookieEncryption('userDetails', cookiePayload);

          const _id = userObj._id ? userObj._id : userObj.userId;
          const userId = userObj._id ? userObj._id : userObj.userId;

          // Cookie uses the slim payload (avoids ~4 KB overflow losing the JWT).
          // Redux needs the full userObj so components can read partnerAdmin, region, etc.
          // Strip only password (should not reach client anyway); use cookie-sourced token.
          const { password: _pw, ...userObjSafe } = userObj;
          this.setUserDetails({
            name: 'logInUser',
            data: {
              ...userObjSafe,
              _id,
              userId,
              fullName: cookiePayload.fullName,
              // Sidebar and mentee profile gate Milestones on a top-level partnerType, which only login returns.
              partnerType: cookiePayload.partnerType,
              token: auth?.token,
            },
          });

          dispatch.onBoardingModel.setData({
            name: 'onboardingSteps',
            data,
          });
        }
      });
    },

    async userCreateInquiry({ inquiryData, handleNavigate, setisDisable }) {
      return axiosClient
        .post('auth/createInquiry', inquiryData)
        .then(res => {
          setisDisable(false);
          if (res.data.code === 200) {
            dispatch.toastModel.showToast({
              value: 'Success',
              message: res.data.msg,
            });
            handleNavigate('/thankyou');
          } else {
            dispatch.toastModel.showToast({
              value: 'Danger',
              message: res.data.msg,
            });
          }
        })
        .catch(err => {
          return err;
        });
    },

    async changePasswordUser({ payload, handleSettingOpen }) {
      return await axiosClient
        .post('auth/changePassword', payload)
        .then(res => {
          if (res.data.code === 200) {
            dispatch.toastModel.showToast({
              value: 'Success',
              message: res.data.msg,
            });
            handleSettingOpen();
          } else {
            if (res.data.code !== 400) {
              dispatch.toastModel.showToast({
                value: 'Danger',
                message: res.data.msg,
              });
            } else {
              return {
                error: true,
                state: res.data.code,
                message: res.data.msg,
              };
            }
          }
        })
        .catch(err => {
          return err;
        });
    },

    async userProfileUpdate(payload) {
      return axiosClient
        .post('auth/userProfileUpdate', payload)
        .then(res => {
          if (res.data.code === 200) {
            dispatch.authModel.getUserDetails({});
            dispatch.toastModel.showToast({
              value: 'Success',
              message: res.data.msg,
            });
          } else {
            dispatch.toastModel.showToast({
              value: 'Danger',
              message: res.data.msg,
            });
          }
        })
        .catch(err => {
          return err;
        });
    },

    async updateMessageNotificationStatus(payload) {
      return axiosClient.post('auth/updateMessageNotificationStatus', payload).catch(err => {
        return err;
      });
    },
  }),
};

export default authModel;
